import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import { FileText, Search, MessageCircle, Menu, ChevronRight, ChevronDown, Eye, Calendar, Globe, ExternalLink, Sparkles, BookOpen, Folder, ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PublicChatWidget from '../components/PublicChatWidget';
import ProactiveMessages from '../components/ProactiveMessages';
import PortalBottomNav from '../components/PortalBottomNav';

// Strip HTML helper
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Translations for public portal
const translations = {
  en: {
    searchPlaceholder: 'Search knowledge base...',
    allDocuments: 'All Documents',
    categories: 'Categories',
    clear: 'Clear',
    noDocuments: 'No documents found',
    tryAdjusting: 'Try adjusting your search or filters',
    views: 'views',
    needHelp: 'Need help? Contact us at'
  },
  fr: {
    searchPlaceholder: 'Rechercher dans la base de connaissances...',
    allDocuments: 'Tous les documents',
    categories: 'Catégories',
    clear: 'Effacer',
    noDocuments: 'Aucun document trouvé',
    tryAdjusting: 'Essayez d\'ajuster votre recherche ou vos filtres',
    views: 'vues',
    needHelp: 'Besoin d\'aide ? Contactez-nous à'
  }
};

const PublicPortal = ({ customDomain }) => {
  const { subdomain: paramSubdomain } = useParams();
  const [searchParams] = useSearchParams();
  const subdomain = customDomain || paramSubdomain;
  const accessKey = searchParams.get('key') || '';
  const [workspace, setWorkspace] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [initialFormSlug, setInitialFormSlug] = useState(null);
  const [initialBooking, setInitialBooking] = useState(null); // {url, label}
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [language, setLanguage] = useState(localStorage.getItem('portal_language') || 'en');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(`portal_view_${paramSubdomain || customDomain}`) || 'popular');

  useEffect(() => {
    localStorage.setItem(`portal_view_${paramSubdomain || customDomain}`, viewMode);
  }, [viewMode, paramSubdomain, customDomain]);

  const t = (key) => translations[language][key] || translations.en[key] || key;

  const docLink = (docId) => {
    const base = customDomain ? `/document/${docId}` : `/portal/${subdomain}/document/${docId}`;
    return accessKey ? `${base}?key=${accessKey}` : base;
  };

  useEffect(() => {
    fetchWorkspaceData();
  }, [subdomain, selectedCategory, selectedTag, searchQuery]);

  const fetchWorkspaceData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedTag) params.append('tag', selectedTag);
      if (searchQuery) params.append('search', searchQuery);
      if (accessKey) params.append('key', accessKey);

      const keyParam = accessKey ? `?key=${accessKey}` : '';
      
      const [wsRes, docsRes, catsRes] = await Promise.all([
        axios.get(`${API_URL}/public/workspaces/${subdomain}${keyParam}`),
        axios.get(`${API_URL}/public/workspaces/${subdomain}/documents?${params.toString()}`),
        axios.get(`${API_URL}/public/workspaces/${subdomain}/categories${keyParam}`)
      ]);
      
      setWorkspace(wsRes.data);
      setDocuments(docsRes.data.documents);
      setAvailableTags(docsRes.data.available_tags || []);
      
      // Build category tree from flat list
      const cats = catsRes.data.categories || [];
      const categoryTree = buildCategoryTree(cats);
      setCategories(categoryTree);
      
      // Auto-expand all categories initially
      const expandedState = {};
      cats.forEach(cat => {
        expandedState[cat.id] = true;
      });
      setExpandedCategories(expandedState);
    } catch (error) {
      console.error('Error fetching workspace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildCategoryTree = (categories) => {
    const catMap = {};
    categories.forEach(cat => {
      cat.children = [];
      catMap[cat.id] = cat;
    });

    const tree = [];
    categories.forEach(cat => {
      if (cat.parent_id && catMap[cat.parent_id]) {
        catMap[cat.parent_id].children.push(cat);
      } else {
        tree.push(cat);
      }
    });

    return tree;
  };

  const getDocumentCountForCategory = (categoryName) => {
    return documents.filter(doc => doc.category === categoryName).length;
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'fr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('portal_language', newLang);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-lg text-zinc-600">Loading...</div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">Knowledge Base Not Found</h2>
          <p className="text-zinc-600">The requested knowledge base does not exist or is not public.</p>
        </div>
      </div>
    );
  }

  const settings = workspace.settings || {};
  const primaryColor = settings.primary_color || '#7c3aed';
  const portalLayout = settings.portal_layout || 'modern';
  const portalTheme = settings.portal_theme || 'light';
  const showSearch = settings.show_search !== false;
  const showSidebar = settings.show_categories_sidebar !== false;
  const showAiChat = settings.show_ai_chat !== false;
  const footerText = settings.footer_text || '';
  const aiButtonText = settings.ai_button_text || 'Ask AI';
  const enableLanguageToggle = settings.enable_language_toggle !== false;
  
  // Theme colors
  const isDarkTheme = portalTheme === 'dark' || (portalTheme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const bgColor = isDarkTheme ? 'bg-zinc-900' : 'bg-zinc-50';
  const cardBg = isDarkTheme ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200';
  const textColor = isDarkTheme ? 'text-zinc-100' : 'text-zinc-900';
  const textMuted = isDarkTheme ? 'text-zinc-400' : 'text-zinc-600';
  const headerBg = isDarkTheme ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200';

  // Render category tree
  const renderCategoryTree = (cats, level = 0) => {
    return cats.map(cat => {
      const docCount = getDocumentCountForCategory(cat.name);
      const hasChildren = cat.children && cat.children.length > 0;
      const isExpanded = expandedCategories[cat.id];
      
      return (
        <div key={cat.id} className="mb-1">
          <button
            onClick={() => {
              if (hasChildren) {
                toggleCategory(cat.id);
              }
              setSelectedCategory(cat.name);
            }}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between group ${
              selectedCategory === cat.name
                ? 'bg-violet-100 text-violet-700 font-semibold'
                : isDarkTheme ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
            }`}
            style={{ paddingLeft: `${level * 0.75 + 0.75}rem` }}
            data-testid={`category-${cat.id}`}
          >
            <div className="flex items-center gap-2 flex-1">
              {hasChildren && (
                isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
              <span className="flex-1">{cat.name}</span>
            </div>
            <span className={`text-xs ml-2 ${isDarkTheme ? 'text-zinc-500' : 'text-zinc-500'}`}>{docCount}</span>
          </button>
          {hasChildren && isExpanded && (
            <div className="mt-1">
              {renderCategoryTree(cat.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Generate document link based on whether it's custom domain or not
  const getDocumentLink = (docId) => {
    return customDomain ? `/document/${docId}` : `/portal/${subdomain}/document/${docId}`;
  };

  // Render document based on layout
  const renderDocument = (doc) => {
    const content = stripHtml(doc.content);
    
    if (portalLayout === 'minimal') {
      return (
        <Link
          key={doc.id}
          to={getDocumentLink(doc.id)}
          className={`block p-4 ${cardBg} border rounded-lg hover:shadow-sm transition-all`}
          data-testid={`doc-${doc.id}`}
        >
          <h3 className={`font-semibold ${textColor}`}>{doc.title}</h3>
          <p className={`text-sm ${textMuted} mt-1 line-clamp-1`}>{content.substring(0, 100)}...</p>
        </Link>
      );
    }
    
    if (portalLayout === 'classic') {
      return (
        <Link
          key={doc.id}
          to={docLink(doc.id)}
          className={`flex items-center gap-4 p-4 ${cardBg} border rounded-lg hover:shadow-sm transition-all`}
          data-testid={`doc-${doc.id}`}
        >
          <div
            className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <FileText className="h-6 w-6" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold ${textColor} truncate`}>{doc.title}</h3>
            <p className={`text-sm ${textMuted} line-clamp-1`}>{content.substring(0, 100)}...</p>
          </div>
          <div className={`text-xs ${textMuted} flex items-center gap-1`}>
            <Eye className="h-3 w-3" />
            {doc.views}
          </div>
        </Link>
      );
    }
    
    if (portalLayout === 'card') {
      return (
        <Link
          key={doc.id}
          to={docLink(doc.id)}
          className={`${cardBg} border rounded-xl p-8 hover:shadow-md transition-all group`}
          data-testid={`doc-${doc.id}`}
        >
          <div
            className="h-16 w-16 rounded-xl flex items-center justify-center mb-6"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <FileText className="h-8 w-8" style={{ color: primaryColor }} />
          </div>
          <h3 className={`text-xl font-bold ${textColor} mb-3 group-hover:text-violet-600 transition-colors`}>
            {doc.title}
          </h3>
          <p className={`${textMuted} line-clamp-3 mb-4`}>{content.substring(0, 150)}...</p>
          {doc.category && (
            <span className="px-3 py-1 text-xs rounded-full bg-zinc-100 text-zinc-600">{doc.category}</span>
          )}
        </Link>
      );
    }
    
    // Default: modern (refined)
    return (
      <Link
        key={doc.id}
        to={docLink(doc.id)}
        className={`${cardBg} border rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group`}
        data-testid={`doc-${doc.id}`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}12` }}
          >
            <FileText className="h-5 w-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            {doc.category && (
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primaryColor }}>
                {doc.category}
              </span>
            )}
            <h3 className={`text-base font-bold ${textColor} leading-snug line-clamp-2 group-hover:opacity-80 transition-opacity mt-0.5`} style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>
              {doc.title}
            </h3>
          </div>
        </div>
        <p className={`text-sm ${textMuted} line-clamp-2 mb-4 leading-relaxed`}>{content.substring(0, 140)}…</p>
        <div className={`flex items-center justify-between text-xs ${textMuted}`}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1"><Eye className="h-3 w-3" />{doc.views || 0}</div>
            <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(doc.created_at).toLocaleDateString()}</div>
          </div>
          <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" style={{ color: primaryColor }} />
        </div>
      </Link>
    );
  };

  // Get grid class based on layout
  const getGridClass = () => {
    switch (portalLayout) {
      case 'minimal':
        return 'space-y-2';
      case 'classic':
        return 'space-y-3';
      case 'card':
        return 'grid grid-cols-1 md:grid-cols-2 gap-6';
      default:
        return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6';
    }
  };

  // Top popular articles (most viewed)
  const popularDocs = [...documents].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 6);
  const showHero = !searchQuery && !selectedCategory && !selectedTag;
  const defaultView = settings.portal_default_view || 'popular';
  // If admin changed default since last visit, respect saved user preference only if it was explicit; otherwise use default
  // Simpler: just use whatever's in state — already loaded from localStorage with fallback to 'popular'
  const effectiveView = showHero ? viewMode : 'all'; // when filtering, always show flat results
  const heroHeading = settings.hero_heading || `${workspace.name} Helpdesk`;
  const heroSubheading = settings.hero_subheading || settings.welcome_message || 'Find answers, browse guides, and chat with our AI assistant.';

  return (
    <div className={`min-h-screen ${bgColor}`} data-testid="public-portal">
      {/* Compact Top Bar */}
      <header className={`${headerBg} border-b sticky top-0 z-40 backdrop-blur-md bg-opacity-90`}>
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {showSidebar && (
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`lg:hidden p-2 rounded-md ${isDarkTheme ? 'hover:bg-zinc-700' : 'hover:bg-zinc-100'}`}
                >
                  <Menu className={`h-5 w-5 ${textColor}`} />
                </button>
              )}
              <Link to={customDomain ? '/' : `/portal/${subdomain}${accessKey ? `?key=${accessKey}` : ''}`} className="flex items-center gap-2.5">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt={workspace.name} className="h-8" />
                ) : (
                  <div className="h-9 w-9 rounded-xl flex items-center justify-center shadow-sm" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
                    <BookOpen className="h-4.5 w-4.5 text-white" strokeWidth={2.5} />
                  </div>
                )}
                <span className={`font-bold ${textColor} tracking-tight`} style={{ fontFamily: 'Manrope, sans-serif' }}>{workspace.name}</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              {settings.header_buttons && settings.header_buttons.length > 0 && settings.header_buttons.map((btn, idx) => (
                <a key={idx} href={btn.url} target="_blank" rel="noopener noreferrer"
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDarkTheme ? 'hover:bg-zinc-700 text-zinc-300' : 'hover:bg-zinc-100 text-zinc-700'}`}>
                  <ExternalLink className="h-3 w-3" />{btn.label}
                </a>
              ))}
              {enableLanguageToggle && (
                <button onClick={toggleLanguage}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDarkTheme ? 'hover:bg-zinc-700 text-zinc-300' : 'hover:bg-zinc-100 text-zinc-700'}`}
                  data-testid="portal-language-toggle">
                  <Globe className="h-3.5 w-3.5" />{language === 'en' ? 'FR' : 'EN'}
                </button>
              )}
              {showAiChat && (
                <Button
                  onClick={() => setChatOpen(!chatOpen)}
                  className="h-9 rounded-full px-4 text-white shadow-sm hover:shadow-md transition-all"
                  style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
                  data-testid="open-chat-button"
                >
                  <Sparkles className="h-4 w-4 mr-1.5" />{aiButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero — always on landing for consistent layout */}
      {showHero && showSearch && (
        <section className="relative overflow-hidden" data-testid="portal-hero">
          <div
            className="absolute inset-0"
            style={{ background: `radial-gradient(circle at 30% 20%, ${primaryColor}22 0%, transparent 50%), radial-gradient(circle at 80% 70%, ${primaryColor}18 0%, transparent 50%)` }}
          />
          <div className="relative max-w-3xl mx-auto px-6 py-12 sm:py-14 text-center">
            <h1 className={`text-4xl sm:text-5xl font-bold ${textColor} leading-[1.1] tracking-tight`} style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.025em' }}>
              {heroHeading}
            </h1>
            <p className={`mt-4 text-base sm:text-lg ${textMuted} max-w-xl mx-auto leading-relaxed`}>
              {heroSubheading}
            </p>
            <div className="mt-7 max-w-xl mx-auto">
              <div className="relative">
                <Search className={`absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 ${textMuted}`} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className={`h-14 pl-14 pr-5 rounded-2xl shadow-lg border-0 text-base ${isDarkTheme ? 'bg-zinc-800 text-white placeholder:text-zinc-500' : 'bg-white'}`}
                  style={{ boxShadow: `0 12px 40px -12px ${primaryColor}30, 0 4px 12px rgba(0,0,0,0.04)` }}
                  data-testid="public-search-input"
                />
              </div>
              {availableTags.length > 0 && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <span className={`text-xs ${textMuted}`}>Popular:</span>
                  {availableTags.slice(0, 5).map(tag => (
                    <button key={tag} onClick={() => setSelectedTag(tag)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all hover:scale-105 ${isDarkTheme ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-white text-zinc-700 hover:shadow-sm'}`}
                      style={{ border: `1px solid ${primaryColor}22` }}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* View Mode Tabs — only on landing */}
      {showHero && documents.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 pt-2 pb-1">
          <div className={`inline-flex p-1 rounded-xl ${isDarkTheme ? 'bg-zinc-800' : 'bg-zinc-100'}`} data-testid="view-mode-tabs">
            {[
              { id: 'popular', label: '⭐ Popular', desc: 'Featured & most-viewed' },
              { id: 'categories', label: '📁 By Category', desc: 'Browse by topic' },
              { id: 'all', label: '📚 All Articles', desc: 'Full list' },
            ].map(m => {
              const active = viewMode === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setViewMode(m.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${active ? 'bg-white text-zinc-900 shadow-sm' : isDarkTheme ? 'text-zinc-400 hover:text-zinc-200' : 'text-zinc-600 hover:text-zinc-900'}`}
                  style={active ? { color: primaryColor } : {}}
                  data-testid={`view-mode-${m.id}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Compact search bar — only when filter active */}
      {!showHero && showSearch && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <div className="relative">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 ${textMuted}`} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className={`h-11 pl-11 rounded-xl ${isDarkTheme ? 'bg-zinc-800 border-zinc-700 text-white' : ''}`}
              data-testid="public-search-input"
            />
          </div>
        </div>
      )}

      {/* Category Tiles — show on landing when viewMode = categories or popular */}
      {showHero && categories.length > 0 && (effectiveView === 'categories' || effectiveView === 'popular') && (
        <section className="max-w-6xl mx-auto px-6 py-6" data-testid="portal-category-tiles">
          <h2 className={`text-xs font-bold uppercase tracking-wider ${textMuted} mb-4`}>{effectiveView === 'categories' ? 'All categories' : 'Browse by category'}</h2>
          <div className={`grid gap-3 ${effectiveView === 'categories' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'}`}>
            {(effectiveView === 'categories' ? categories : categories.slice(0, 10)).map(cat => {
              const count = getDocumentCountForCategory(cat.name);
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`text-left ${cardBg} border rounded-2xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group`}
                  data-testid={`cat-tile-${cat.id}`}
                >
                  <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${primaryColor}12` }}>
                    <Folder className="h-4.5 w-4.5" style={{ color: primaryColor }} />
                  </div>
                  <div className={`font-semibold text-sm ${textColor} line-clamp-1`} style={{ fontFamily: 'Manrope, sans-serif' }}>{cat.name}</div>
                  <div className={`text-xs ${textMuted} mt-0.5`}>{count} article{count === 1 ? '' : 's'}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Popular Articles strip — only in Popular view */}
      {showHero && popularDocs.length >= 3 && effectiveView === 'popular' && (
        <section className="max-w-6xl mx-auto px-6 py-4" data-testid="portal-popular">
          <h2 className={`text-xs font-bold uppercase tracking-wider ${textMuted} mb-4 flex items-center gap-2`}>
            <TrendingUp className="h-3.5 w-3.5" />Popular articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {popularDocs.slice(0, 3).map(doc => (
              <Link key={`pop-${doc.id}`} to={docLink(doc.id)}
                className={`${cardBg} border rounded-2xl p-5 hover:shadow-md transition-all group`}>
                {doc.category && (
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primaryColor }}>{doc.category}</span>
                )}
                <h3 className={`text-base font-bold ${textColor} mt-1 line-clamp-2 group-hover:opacity-80`} style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>
                  {doc.title}
                </h3>
                <div className={`text-xs ${textMuted} mt-2 flex items-center gap-1.5`}>
                  <Eye className="h-3 w-3" />{doc.views || 0} views
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Main Content — always shown for consistent layout */}
      <div className="max-w-6xl mx-auto flex gap-6 px-6 py-6">
        {/* Category Sidebar — always visible on landing */}
        {showSidebar && (sidebarOpen || (typeof window !== 'undefined' && window.innerWidth >= 1024)) && (
          <aside className="w-60 flex-shrink-0 hidden lg:block" data-testid="category-sidebar">
              <div className={`${cardBg} border rounded-2xl p-4 sticky top-20`}>
                <h3 className={`text-xs font-bold uppercase tracking-wider ${textMuted} mb-3 flex items-center justify-between`}>
                  <span>{t('categories')}</span>
                  {(selectedCategory || selectedTag) && (
                    <button onClick={() => { setSelectedCategory(''); setSelectedTag(''); }}
                      className="text-xs font-semibold normal-case tracking-normal" style={{ color: primaryColor }}>
                      {t('clear')}
                    </button>
                  )}
                </h3>
                <div className="space-y-0.5">
                  <button onClick={() => setSelectedCategory('')}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      !selectedCategory ? 'font-semibold' : isDarkTheme ? 'text-zinc-300 hover:bg-zinc-700' : 'text-zinc-700 hover:bg-zinc-100'
                    }`}
                    style={!selectedCategory ? { backgroundColor: `${primaryColor}15`, color: primaryColor } : {}}
                  >
                    {t('allDocuments')}
                  </button>
                  {categories.length > 0 && renderCategoryTree(categories)}
                </div>
                {availableTags.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-zinc-100">
                    <h3 className={`text-xs font-bold uppercase tracking-wider ${textMuted} mb-2`}>Tags</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {availableTags.map(tag => (
                        <button key={tag} onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                            selectedTag === tag
                              ? 'text-white'
                              : isDarkTheme ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                          }`}
                          style={selectedTag === tag ? { backgroundColor: primaryColor } : {}}
                        >
                          #{tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          )}

          {/* Documents Area */}
          <main className="flex-1 min-w-0">
          {(selectedCategory || selectedTag || searchQuery) && (
            <div className="mb-5 flex items-center gap-2 flex-wrap">
              <span className={`text-xs ${textMuted}`}>Showing:</span>
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: primaryColor }}>
                  {selectedCategory}
                  <button onClick={() => setSelectedCategory('')} className="hover:opacity-70">×</button>
                </span>
              )}
              {selectedTag && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: primaryColor }}>
                  #{selectedTag}
                  <button onClick={() => setSelectedTag('')} className="hover:opacity-70">×</button>
                </span>
              )}
              {searchQuery && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${isDarkTheme ? 'bg-zinc-700 text-zinc-200' : 'bg-zinc-100 text-zinc-700'}`}>
                  &quot;{searchQuery}&quot;
                  <button onClick={() => setSearchQuery('')} className="hover:opacity-70">×</button>
                </span>
              )}
              <span className={`text-xs ${textMuted} ml-1`}>· {documents.length} result{documents.length === 1 ? '' : 's'}</span>
            </div>
          )}

          {documents.length > 0 ? (
            effectiveView === 'categories' && showHero ? (
              <div className={`${cardBg} border rounded-2xl p-10 text-center`}>
                <Folder className="h-10 w-10 mx-auto mb-3" style={{ color: primaryColor }} />
                <h3 className={`text-base font-semibold ${textColor} mb-1`}>Browse by category</h3>
                <p className={`text-sm ${textMuted}`}>Pick a category above or from the sidebar to see its articles.</p>
              </div>
            ) : (
              <div className={getGridClass()} data-testid="documents-grid">
                {(effectiveView === 'popular' && showHero ? documents.slice(0, 9) : documents).map(doc => renderDocument(doc))}
              </div>
            )
          ) : (
            <div className={`text-center py-16 ${cardBg} border rounded-2xl`} data-testid="empty-state">
              <div className="h-16 w-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                <FileText className="h-8 w-8" style={{ color: primaryColor }} />
              </div>
              <h3 className={`text-lg font-semibold ${textColor} mb-1`} style={{ fontFamily: 'Manrope, sans-serif' }}>{t('noDocuments')}</h3>
              <p className={textMuted}>{t('tryAdjusting')}</p>
            </div>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className={`${headerBg} border-t mt-20`}>
        <div className={`max-w-6xl mx-auto px-6 py-6 text-center text-xs ${textMuted}`}>
          {footerText ? <span>{footerText}</span> : settings.contact_email ? (
            <>
              {t('needHelp')}{' '}
              <a href={`mailto:${settings.contact_email}`} className="font-semibold" style={{ color: primaryColor }}>
                {settings.contact_email}
              </a>
            </>
          ) : (
            <span>© {new Date().getFullYear()} {workspace.name}</span>
          )}
        </div>
      </footer>

      {/* Chat Widget */}
      {showAiChat && chatOpen && (
        <PublicChatWidget
          subdomain={subdomain}
          workspaceName={workspace.name}
          primaryColor={primaryColor}
          onClose={() => { setChatOpen(false); setInitialFormSlug(null); setInitialBooking(null); }}
          settings={settings}
          accessKey={accessKey}
          initialFormSlug={initialFormSlug}
          initialBooking={initialBooking}
        />
      )}

      {/* Proactive Messages — show when chat closed */}
      {showAiChat && !chatOpen && (settings.chat_proactive_messages || []).length > 0 && (
        <ProactiveMessages
          subdomain={subdomain}
          primaryColor={primaryColor}
          messages={settings.chat_proactive_messages || []}
          onOpenWidget={() => setChatOpen(true)}
          onOpenForm={(slug) => { setInitialFormSlug(slug); setChatOpen(true); }}
          onOpenBooking={(url, label) => { setInitialBooking({ url, label }); setChatOpen(true); }}
        />
      )}
    </div>
  );
};

export default PublicPortal;
